import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  Content,
  ExpandableSection,
  Flex,
  FlexItem,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import type { UseInvoiceAutopayReturn } from '../../../hooks/useInvoiceAutopay';
import type { InvoiceAutopayTermsText, InvoiceAutopayUpdateRequest } from '../../../types/models/Wallet';
import type { ApiResponseInvoiceAutopay } from '../../../types/api/ApiResponseWallet';
import { formatDateTime } from '../../../utils/dateFormat';

type TermsLanguage = 'en' | 'fr';

const HINT_ID = 'invoice-autopay-hint';
const ACCEPT_TITLE_ID = 'invoice-autopay-accept-title';
const TURN_OFF_TITLE_ID = 'invoice-autopay-turn-off-title';
const CONFIRMATION_EMAILED = 'A confirmation was emailed to your billing contact and users.';

/** The unpaid-invoice warning — rendered from the API only, never collapsible nor dismissible. */
const TermsWarning: React.FunctionComponent<{ text: InvoiceAutopayTermsText }> = ({ text }) => (
  <Alert variant="warning" isInline title={text.warning.title}>
    {text.warning.text}
  </Alert>
);

const TermsPoints: React.FunctionComponent<{ text: InvoiceAutopayTermsText }> = ({ text }) => (
  <List>
    {text.points.map((point) => (
      <ListItem key={point}>{point}</ListItem>
    ))}
  </List>
);

const TermsLanguageToggle: React.FunctionComponent<{
  language: TermsLanguage;
  onChange: (language: TermsLanguage) => void;
}> = ({ language, onChange }) => (
  <ToggleGroup isCompact aria-label="Language of the terms">
    <ToggleGroupItem text="FR" isSelected={language === 'fr'} onChange={() => onChange('fr')} />
    <ToggleGroupItem text="EN" isSelected={language === 'en'} onChange={() => onChange('en')} />
  </ToggleGroup>
);

interface InvoiceAutopayCardProps {
  /** The parent's `useInvoiceAutopay()`, so a saved-card change can refresh `has_saved_card`. */
  autopayState: UseInvoiceAutopayReturn;
}

/**
 * "Automatic payment of all invoices" — the organization's opt-in to having every outstanding invoice,
 * including those issued outside the Manager, paid automatically (wallet.md, "Automatic payment of all
 * invoices"). Saved separately from the renewal opt-in above it, but it covers renewal invoices too: the
 * renewal workflow pays them under either opt-in, from 7 days before expiry. The consent is informed: the
 * backend's terms are readable at any time, its unpaid-invoice warning is always shown, and the switch
 * never saves directly — turning on goes through an acceptance modal that echoes the displayed
 * `terms.version`, turning off through a confirmation that repeats the warning. The backend refuses the
 * change under an impersonation session, so the controls are disabled then; staff still see the setting
 * and the terms.
 */
const InvoiceAutopayCard: React.FunctionComponent<InvoiceAutopayCardProps> = ({ autopayState }) => {
  const { autopay, loading, error, refetch, setAutopay } = autopayState;
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { isImpersonating } = useImpersonation();
  const [language, setLanguage] = useState<TermsLanguage>('en');
  const [openModal, setOpenModal] = useState<'accept' | 'turnOff' | null>(null);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isTermsExpanded, setIsTermsExpanded] = useState(false);

  // New terms (the refetch after a refused PUT): a tick given for the previous version no longer counts.
  const termsVersion = autopay?.terms.version;
  useEffect(() => {
    setHasAccepted(false);
  }, [termsVersion]);

  const openAcceptModal = () => {
    setHasAccepted(false);
    setSubmitError(null);
    setOpenModal('accept');
  };

  const openTurnOffModal = () => {
    setSubmitError(null);
    setOpenModal('turnOff');
  };

  const closeModal = () => {
    if (!isSubmitting) {
      setOpenModal(null);
    }
  };

  const submit = useCallback(
    async (
      request: InvoiceAutopayUpdateRequest,
      successTitle: string,
      successDescription: string = CONFIRMATION_EMAILED,
    ) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const response = await apiFetch('/clients/invoice-autopay', {
          method: 'PUT',
          body: JSON.stringify(request),
        });
        // JSend: failures arrive as status:'error' at HTTP 200 with a display-safe message.
        const body = (await response.json().catch(() => null)) as ApiResponseInvoiceAutopay | null;
        if (body?.status !== 'success' || !body.data?.invoice_autopay) {
          if (body?.data?.accepted_terms_version !== undefined) {
            // The terms changed while the user read them: load the new ones into the open modal.
            void refetch();
          }
          throw new Error(
            body?.data?.accepted_terms_version ??
              body?.data?.enabled ??
              body?.data?.body ??
              body?.message ??
              `The request failed (HTTP ${response.status}).`,
          );
        }

        setAutopay(body.data.invoice_autopay);
        setOpenModal(null);
        addNotification({
          title: successTitle,
          srTitle: 'Success alert',
          description: successDescription,
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
        // Also shown in the modal: its backdrop covers the notification drawer.
        setSubmitError(message);
        addNotification({
          title: 'Could not update automatic payment of invoices',
          srTitle: 'Error alert',
          description: message,
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [apiFetch, refetch, setAutopay, addNotification, setDrawerOpen],
  );

  const renderBody = () => {
    if (loading && autopay === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the automatic invoice payment setting" />
        </Bullseye>
      );
    }
    if (error && autopay === null) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Unable to load the automatic invoice payment setting"
          actionLinks={
            <Button variant="link" isInline onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      );
    }
    if (autopay === null) {
      return null;
    }

    const text = autopay.terms[language];
    const hasNewerTerms = autopay.enabled && autopay.accepted_terms_version !== autopay.terms.version;

    return (
      <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
        <FlexItem>
          <TermsWarning text={text} />
        </FlexItem>

        {hasNewerTerms && (
          <FlexItem>
            <Alert
              variant="info"
              isInline
              title="The terms have changed"
              actionLinks={
                <Button variant="link" isInline onClick={openAcceptModal} isDisabled={isImpersonating}>
                  Review and accept
                </Button>
              }
            >
              Version {autopay.terms.version} of the terms replaces the version you accepted
              {autopay.accepted_terms_version !== null && ` (${autopay.accepted_terms_version})`}.
            </Alert>
          </FlexItem>
        )}

        <FlexItem>
          <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
            <FlexItem>
              <Switch
                id="invoice-autopay"
                label="Pay all my invoices automatically"
                aria-describedby={HINT_ID}
                isChecked={autopay.enabled}
                isDisabled={isImpersonating || isSubmitting}
                onChange={(_event, checked) => (checked ? openAcceptModal() : openTurnOffModal())}
              />
            </FlexItem>
            {isSubmitting && (
              <FlexItem>
                <Spinner size="sm" aria-label="Updating automatic payment of invoices" />
              </FlexItem>
            )}
          </Flex>
          <Content component="small" id={HINT_ID} className="pf-v6-u-display-block pf-v6-u-mt-sm">
            We pay your organization&apos;s unpaid invoices — including invoices issued outside the Manager — with your
            prepaid balance if it covers the whole invoice, otherwise with your default saved card. Each invoice is paid
            on the day it is issued (we check once a day). Turning this on also pays the invoices that are unpaid today,
            at the next daily run. Domain renewal invoices are paid too, on the renewal schedule: from 7 days before the
            domain expires.
          </Content>
          {autopay.changed_at !== null && (
            <Content component="small" className="pf-v6-u-display-block pf-v6-u-mt-xs">
              Last changed {formatDateTime(autopay.changed_at)}.
            </Content>
          )}
        </FlexItem>

        {autopay.enabled && !autopay.has_saved_card && (
          <FlexItem>
            <Alert variant="warning" isInline title="No saved card">
              Automatic payment relies on your prepaid balance alone: an invoice the balance doesn&apos;t fully cover
              won&apos;t be paid automatically. Add a card, or keep the balance topped up.
            </Alert>
          </FlexItem>
        )}

        {isImpersonating && (
          <FlexItem>
            <Content component="small">
              Only the organization itself can change this setting, not an impersonation session.
            </Content>
          </FlexItem>
        )}

        <FlexItem>
          <ExpandableSection
            toggleText={`Read the terms (version ${autopay.terms.version})`}
            isExpanded={isTermsExpanded}
            onToggle={(_event, expanded) => setIsTermsExpanded(expanded)}
          >
            <TermsPoints text={text} />
          </ExpandableSection>
        </FlexItem>
      </Flex>
    );
  };

  return (
    <>
      <Card>
        <CardHeader
          actions={
            autopay !== null
              ? { actions: <TermsLanguageToggle language={language} onChange={setLanguage} /> }
              : undefined
          }
        >
          <CardTitle>Automatic payment of all invoices</CardTitle>
        </CardHeader>
        <CardBody>{renderBody()}</CardBody>
      </Card>

      {autopay !== null && (
        <Modal variant="medium" isOpen={openModal === 'accept'} onClose={closeModal} aria-labelledby={ACCEPT_TITLE_ID}>
          <ModalHeader title="Turn on automatic payment of all invoices" labelId={ACCEPT_TITLE_ID} />
          <ModalBody>
            <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
              <FlexItem alignSelf={{ default: 'alignSelfFlexEnd' }}>
                <TermsLanguageToggle language={language} onChange={setLanguage} />
              </FlexItem>
              <FlexItem>
                <TermsPoints text={autopay.terms[language]} />
              </FlexItem>
              <FlexItem>
                {/* Ours, not part of the terms the customer accepts. */}
                <Content component="p">
                  Once on, invoices are paid on the day they are issued, and the invoices unpaid today are paid at the
                  next daily run.
                </Content>
              </FlexItem>
              <FlexItem>
                <TermsWarning text={autopay.terms[language]} />
              </FlexItem>
              <FlexItem>
                <Checkbox
                  id="invoice-autopay-accept-terms"
                  label={`I have read and accept these terms (version ${autopay.terms.version}), including the warning above.`}
                  isChecked={hasAccepted}
                  isDisabled={isSubmitting}
                  onChange={(_event, checked) => setHasAccepted(checked)}
                />
              </FlexItem>
              {submitError && (
                <FlexItem>
                  <Alert variant="danger" isInline title={submitError} />
                </FlexItem>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              isDisabled={!hasAccepted || isSubmitting || isImpersonating}
              isLoading={isSubmitting}
              onClick={() =>
                void submit(
                  // The version of the terms displayed in this modal, never a constant.
                  { enabled: true, accepted_terms_version: autopay.terms.version },
                  autopay.enabled ? 'New terms accepted' : 'Automatic payment of invoices turned on',
                  // Re-accepting while on starts nothing new: only a turn-on announces the first run.
                  autopay.enabled
                    ? CONFIRMATION_EMAILED
                    : `${CONFIRMATION_EMAILED} Unpaid invoices are paid at the next daily run.`,
                )
              }
            >
              Turn on
            </Button>
            <Button variant="link" onClick={closeModal} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {autopay !== null && (
        <Modal
          variant="small"
          isOpen={openModal === 'turnOff'}
          onClose={closeModal}
          aria-labelledby={TURN_OFF_TITLE_ID}
        >
          <ModalHeader title="Turn off automatic payment?" titleIconVariant="warning" labelId={TURN_OFF_TITLE_ID} />
          <ModalBody>
            <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsMd' }}>
              <FlexItem>
                Your invoices will no longer be paid automatically: you pay them yourself from the Invoices page. Domain
                renewals stay paid automatically only if Automatic renewal payment is on.
              </FlexItem>
              <FlexItem>
                <TermsWarning text={autopay.terms[language]} />
              </FlexItem>
              {submitError && (
                <FlexItem>
                  <Alert variant="danger" isInline title={submitError} />
                </FlexItem>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="danger"
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
              onClick={() => void submit({ enabled: false }, 'Automatic payment of invoices turned off')}
            >
              Turn off
            </Button>
            <Button variant="link" onClick={closeModal} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default InvoiceAutopayCard;
